import { useState, useMemo, useEffect } from 'react';

import Title from '../components/Title';
import Footer from '../components/Footer';

import SelectInput from '../components/SelectInput';
import TextInput from '../components/TextInput';

import { QUESTION_FORMAT, type ScreenProps } from '../types';

const Select = ({
  questionId,
  title,
  description,
  format,
  options,
  required,
  properties,
  onNext,
  onSkip,
  savedAnswer,
  editMode,
}: ScreenProps) => {
  const [selectedValue, setSelectedValue] = useState<string[]>(
    options.filter(o => o.preSelected).map(o => o.value)?.length
      ? options.filter(o => o.preSelected).map(o => o.value)
      : [],
  );

  useEffect(() => {
    if (savedAnswer) {
      setSelectedValue(
        savedAnswer.answers.filter(v => !!v.key).map(v => v.key!),
      );
      setOtherReasonText(
        savedAnswer.answers.filter(v => v.key === 'other')[0]?.value || '',
      );
    }
  }, [savedAnswer]);

  const [otherReasonText, setOtherReasonText] = useState<string>('');
  const isMultiSelect = [
    QUESTION_FORMAT.MULTI_SELECT,
    QUESTION_FORMAT.MULTI_SELECT_VISUAL,
  ].includes(format);
  const isVisual = [
    QUESTION_FORMAT.SINGLE_SELECT_VISUAL,
    QUESTION_FORMAT.MULTI_SELECT_VISUAL,
  ].includes(format);

  const disableNext = useMemo(() => {
    if (properties.allowOther && selectedValue.includes('other')) {
      return (
        (!selectedValue.length && required) ||
        otherReasonText.replace(/\s+$/g, '').trim().length <
          (properties.otherMinCharacter || 5)
      );
    }

    return !selectedValue.length && required;
  }, [
    required,
    properties.allowOther,
    selectedValue,
    otherReasonText,
    properties.otherMinCharacter,
  ]);

  const handleClick = (value: string) => {
    if (value) {
      if (!selectedValue.includes(value)) {
        return isMultiSelect
          ? setSelectedValue([...selectedValue, value])
          : setSelectedValue([value]);
      }

      if (value === 'other') setOtherReasonText('');
      return isMultiSelect
        ? setSelectedValue([...selectedValue.filter(v => v !== value)])
        : setSelectedValue([]);
    }
  };

  const handleOther = (value: string) => {
    setOtherReasonText(value);
  };

  const handleNext = () => {
    const answers = selectedValue
      .filter(v => v !== 'other')
      .map(key => {
        const question = options.find(o => o.value === key)!;
        return {
          key,
          value: question?.label,
          ...(question?.url ? { url: question?.url } : {}),
        };
      });
    if (otherReasonText)
      answers.push({ key: 'other', value: otherReasonText.trim() });
    setSelectedValue([]);
    setOtherReasonText('');
    onNext({ questionId, title, format, answers });
  };

  const handleSkip = () => {
    setSelectedValue([]);
    setOtherReasonText('');
    onSkip();
  };

  return (
    <section className="flex h-screen flex-col justify-between overflow-auto">
      <div>
        <Title title={title} description={description} className="mb-4 mt-6" />
        <div
          className={
            isVisual && properties.visualSize === 'large'
              ? 'grid grid-cols-2 gap-4'
              : ''
          }
        >
          {options.map(option => (
            <SelectInput
              {...option}
              key={option.value}
              url={isVisual ? option.url : undefined}
              handleClick={handleClick}
              isSelected={selectedValue.includes(option.value)}
              visualSize={properties.visualSize}
              isMultiSelect={isMultiSelect}
            />
          ))}
        </div>
        {properties.allowOther && selectedValue.includes('other') && (
          <TextInput
            name={title}
            value={otherReasonText}
            key={'other'}
            min={properties.otherMinCharacter}
            max={properties.otherMaxCharacter}
            onChange={handleOther}
            required={required}
          />
        )}
      </div>
      <Footer
        disablePrimary={disableNext}
        showSecondary={!required || editMode}
        primaryLabel={editMode ? 'Done' : 'Next'}
        secondaryLabel={editMode ? 'Cancel' : 'Skip'}
        handlePrimaryButton={handleNext}
        handleSecondaryButton={handleSkip}
      />
    </section>
  );
};

export default Select;
